import { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelectedOrganization } from "@/hooks/useSelectedOrganization";
import CollapsibleSidebar from "./CollapsibleSidebar";
import MobileNav from "@/components/MobileNav";
import FieldFooter from "@/components/field/FieldFooter";
import { getAllSidebarSections } from "@/lib/dashboardNav";
import "./dashboard-skin.css";

interface DashboardShellProps {
  children: ReactNode;
}

// Longest-prefix match of the current path against the nav so the breadcrumb
// always names the page the user is actually on (admin routes included).
function currentPageLabel(pathname: string): string {
  const items = getAllSidebarSections(true).flatMap((s) => s.items);
  let best: { href: string; label: string } | null = null;
  for (const item of items) {
    const match = item.href === "/dashboard"
      ? pathname === "/dashboard"
      : pathname.startsWith(item.href);
    if (match && (!best || item.href.length > best.href.length)) {
      best = { href: item.href, label: item.label };
    }
  }
  return best?.label ?? "Dashboard";
}

export default function DashboardShell({ children }: DashboardShellProps) {
  const { pathname } = useLocation();
  const { selectedOrganization } = useSelectedOrganization();
  const orgLabel = selectedOrganization?.name || "Personal";
  const pageLabel = currentPageLabel(pathname);

  return (
    <div className="dash-skin h-screen flex bg-background">
      {/* Desktop Sidebar */}
      <div className="hidden md:flex">
        <CollapsibleSidebar />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Content Header */}
        <header className="flex items-center justify-between h-14 px-4 md:px-6 border-b border-neutral-subtle bg-surface">
          {/* Mobile: Hamburger + Logo */}
          <div className="flex items-center gap-2 md:hidden">
            <MobileNav variant="dashboard" />
            <Link to="/" className="font-semibold text-brand-600">
              FHIRfly
            </Link>
          </div>

          {/* Desktop: breadcrumb */}
          <div className="dash-crumb hidden md:block">
            {orgLabel} / <b>{pageLabel}</b>
          </div>

          {/* Right side: quick links */}
          <div className="flex items-center gap-4">
            <Link
              to="/docs"
              className="hidden sm:block text-sm text-text-quiet hover:text-text transition-colors"
            >
              Docs
            </Link>
            <Link
              to="/status"
              className="hidden sm:block text-sm text-text-quiet hover:text-text transition-colors"
            >
              Status
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto flex flex-col">
          <div className="px-4 md:px-6 py-6 flex-1">
            {children}
          </div>
          <div className="ffly ffly--app">
            <FieldFooter variant="compact" />
          </div>
        </main>
      </div>
    </div>
  );
}
